import React, { Component } from 'react';
import { sumBy } from 'lodash';
import Scrollbar from 'react-scrollbars-custom';
import WordLabel from '../WordLabel/WordLabel';
import { Wrapper, ListPlaceholder } from './styled/WordsListStyled';
import KeyphraseStack from './KeyphraseStack/KeyphraseStack';
import { colors, words as wordsUtil } from '../../../utils/utils';
import { DetectionListType, PhraseAlternative, PhraseGroup } from '../../Trainer/types/detection';

const { getPillType, getSortedDetection } = wordsUtil;

interface SortablePhraseGroup extends PhraseGroup {
    count: number;
}

interface KeywordItem {
    text: string;
    count: number;
    points?: number;
}

interface WordsListProps {
    type: DetectionListType;
    words?: PhraseGroup[];
    wordsRendered?: number | null;
    configLoaded: boolean;
    moveSaidDoSayWordsToBottom?: boolean;
    displayDoSayAlternatives?: boolean;
    maxHeight?: number;
}

interface WordsListState {
    maxHeight: number;
}

function toPhraseGroupList(words: PhraseGroup[]): SortablePhraseGroup[] {
    return words.map(word => {
        return {
            ...word,
            text: word.text || '',
            count: word.count || 0,
            alternatives: word.alternatives || [],
            complements: word.complements || []
        };
    });
}

function toKeywordItems(items: PhraseAlternative[] | undefined): KeywordItem[] {
    const list = items || [];
    return list.map(item => ({
        text: item.text || '',
        count: item.count || 0,
        points: item.points
    }));
}

class WordsList extends Component<WordsListProps, WordsListState> {
    static defaultProps: Partial<WordsListProps> = {
        words: [],
        wordsRendered: null,
        moveSaidDoSayWordsToBottom: false,
        displayDoSayAlternatives: true,
        maxHeight: 0
    };

    state = {
        maxHeight: this.props.maxHeight || 0
    };

    _renderContent() {
        const { type, words: rawWords, wordsRendered, configLoaded, moveSaidDoSayWordsToBottom, displayDoSayAlternatives } = this.props;

        const words = rawWords || [];

        if (!configLoaded) {
            return <ListPlaceholder />;
        }

        if (words.length === 0) {
            return null;
        }

        if (type === 'whitelist') {
            const phraseGroups = toPhraseGroupList(words);
            const sortedWords = moveSaidDoSayWordsToBottom ? getSortedDetection(phraseGroups, false, 'asc') : phraseGroups;

            return sortedWords.map((phraseGroup: PhraseGroup) => {
                return (
                    <KeyphraseStack
                        displayDoSayAlternatives={Boolean(displayDoSayAlternatives)}
                        topic={String(phraseGroup.text || '')}
                        key={String(phraseGroup.text || '')}
                        alternatives={toKeywordItems(phraseGroup.alternatives)}
                        complements={toKeywordItems(phraseGroup.complements)}
                    />
                );
            });
        }

        return words.map((word: PhraseGroup, key: number) => {
            if (wordsRendered && key >= wordsRendered) return null;
            if (!word.count) return null;

            const localCount = sumBy(word.alternatives || [], 'count');

            return (
                <WordLabel key={String(word.text || key)} pillType={getPillType(type, word.count)} isClickable={false} count={localCount}>
                    {String(word.text || '')}
                </WordLabel>
            );
        });
    }

    checkHeight(ref: HTMLDivElement | null) {
        if (ref) {
            const maxH = this.props.maxHeight || 0;
            const wordListHeight = ref.getBoundingClientRect().height;
            const newH = wordListHeight < maxH ? wordListHeight : maxH;

            if (this.state.maxHeight !== newH) {
                this.setState({
                    maxHeight: newH
                });
            }
        }
    }

    render() {
        if (this.props.maxHeight === 0) {
            return <Wrapper>{this._renderContent()}</Wrapper>;
        }

        return (
            <Scrollbar
                removeTracksWhenNotUsed
                trackYProps={{
                    style: {
                        width: '4px',
                        right: '2px',
                        background: 'none'
                    }
                }}
                thumbYProps={{
                    style: {
                        background: colors.gray.iron
                    }
                }}
                style={{ height: this.state.maxHeight }}
                scrollerProps={{
                    renderer: (props: { elementRef: (el: HTMLDivElement | null) => void } & React.HTMLAttributes<HTMLDivElement>) => {
                        const { elementRef, ...restProps } = props;
                        return <div {...restProps} ref={elementRef} data-scroller="true" />;
                    }
                }}>
                <Wrapper ref={(ref: HTMLDivElement | null) => this.checkHeight(ref)}>{this._renderContent()}</Wrapper>
            </Scrollbar>
        );
    }
}
export const PureWordsList = WordsList;
export default WordsList;
